import {DetailsHeader} from '@/components/commons/details-header/DetailsHeader'
import {Button} from '@/components/ui/button/Button'
import {DetailsPageWrapper} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {RefreshCw01Icon} from '@/components/ui/icon/Icon'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {ProgressStatusBadge} from '@/features/projects/components/progress-status-badge/ProgressStatusBadge'
import {ProjectDetailsHeaderEarlyClosingMotivation} from '@/features/projects/components/project-details-header-early-closing-motivation/ProjectDetailsHeaderEarlyClosingMotivation'
import {ProjectDetailsHeaderSubTitle} from '@/features/projects/components/project-details-header-subtitle/ProjectDetailsHeaderSubTitle'
import {ProjectDetailsHeaderTitle} from '@/features/projects/components/project-details-header-title/ProjectDetailsHeaderTitle'
import {ProjectDetailsTabs} from '@/features/projects/components/project-details-tabs/ProjectDetailsTabs'
import {ProjectOptionsDropdown} from '@/features/projects/components/project-options-dropdown/ProjectOptionsDropdown'
import {ProjectSuspendedTooltip} from '@/features/projects/components/project-suspended-tooltip/ProjectSuspendedTooltip'
import {ProjectWarningMissingActionBanner} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-action-banner/ProjectWarningMissingActionBanner'
import {useGetProject} from '@/features/projects/queries/useGetProject'
import {ProjectExtended} from '@/features/projects/types'
import {StyledDetailsPageContent} from '@/route-guards/project-details-route/style'
import {useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {Outlet, useOutletContext} from 'react-router-dom'

export const ProjectDetailsLayout = () => {
    const {t} = useTranslation()
    const {project, isEditable} = useOutletContext<{project: ProjectExtended; isEditable: boolean}>()
    const {refetch: refetchProject, isRefetching: isRefetchingProject} = useGetProject({
        urlParams: {projectId: project.id},
        params: {responseType: 'extended'}
    })

    return (
        <DetailsPageWrapper isSuccess>
            <DetailsHeader
                patientId={project.patientId}
                title={<ProjectDetailsHeaderTitle project={project} />}
                subtitle={
                    <Flexbox direction="column" gap={3}>
                        <ProjectDetailsHeaderSubTitle project={project} />
                        {project.status != 'opening' && project.progressStatus && (
                            <ProgressStatusBadge progressStatus={project.progressStatus} />
                        )}
                        <ProjectDetailsHeaderEarlyClosingMotivation project={project} />
                        {project.status && project.status != 'opening' && project.isSuspended && (
                            <ProjectSuspendedTooltip
                                projectStatus={project.status}
                                suspensionDays={
                                    project.suspensions && project.suspensions.length > 0
                                        ? project.suspensions?.[0]?.endDate
                                            ? dayjs(project.suspensions[0].endDate).diff(
                                                  dayjs(project.suspensions?.[0].startDate),
                                                  'day'
                                              )
                                            : 0
                                        : null
                                }
                                suspensionStartDate={
                                    project.suspensions && project.suspensions.length > 0
                                        ? dayjs(project.suspensions?.[0].startDate).toDate()
                                        : null
                                }
                                suspensionEndDate={
                                    project.suspensions &&
                                    project.suspensions.length > 0 &&
                                    project.suspensions?.[0].endDate
                                        ? dayjs(project.suspensions?.[0].endDate).toDate()
                                        : null
                                }
                                suspensionReason={
                                    project.suspensions && project.suspensions.length > 0
                                        ? project.suspensions?.[0].reason
                                        : null
                                }
                            />
                        )}
                        <ProjectWarningMissingActionBanner project={project} />
                    </Flexbox>
                }
                endSlot={
                    <Flexbox gap={2}>
                        <Tooltip
                            content={t('projects:update_project_progress')}
                            rootProps={{open: isRefetchingProject ? false : undefined}}
                            variant="light"
                        >
                            <Button
                                variant="secondary"
                                shape="square"
                                onClick={() => refetchProject()}
                                disabled={isRefetchingProject}
                            >
                                {isRefetchingProject ? (
                                    <Spinner size={12} />
                                ) : (
                                    <RefreshCw01Icon className="custom" size={12} />
                                )}
                            </Button>
                        </Tooltip>
                        <ProjectOptionsDropdown project={project} />
                    </Flexbox>
                }
            />
            <StyledDetailsPageContent>
                <ProjectDetailsTabs projectId={project.id}>
                    <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />}>
                        <Outlet context={{project, isEditable}} />
                    </ScrollArea>
                </ProjectDetailsTabs>
            </StyledDetailsPageContent>
        </DetailsPageWrapper>
    )
}
